import React, { useEffect, useState } from 'react';
import api from '@/lib/api';

/**
 * Renders an image from an authenticated backend endpoint.
 * Participant photos are now served via RBAC-gated streaming (no public URL),
 * so a plain <img src> would fail. This fetches the bytes with the auth client
 * and renders them via an object URL. Blob/data URLs are rendered directly.
 */
export function AuthedImage({ src, alt = '', className = '', fallback = null, ...rest }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let created = null;
    setFailed(false);
    setObjectUrl(null);

    if (!src) return undefined;

    // Local previews / data URIs render directly.
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setObjectUrl(src);
      return undefined;
    }

    const path = src.startsWith('/api/') ? src.slice(4) : src;
    api
      .get(path, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        created = URL.createObjectURL(res.data);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!revoked) setFailed(true);
      });

    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);

  if (failed || !src) return fallback;
  if (!objectUrl) {
    return <div className={`${className} bg-slate-100 animate-pulse`} data-testid="authed-image-loading" />;
  }
  return <img src={objectUrl} alt={alt} className={className} {...rest} />;
}

export default AuthedImage;
